import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { validateEmail } from '../lib/validation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Eye, EyeOff, AlertCircle,
  Shield, CreditCard, Clock, Layers, BarChart3, FileText,
  ArrowLeft
} from 'lucide-react';
import { trackCompleteRegistration, getMetaCookies, setAdvancedMatching } from '../utils/metaPixel';
import { trackOaiRegistration } from '../utils/openaiPixel';

const VALUE_PROPS = [
  {
    icon: Layers,
    title: 'Room-by-Room Tracking',
    desc: 'Every material, every decision — organized by room.',
  },
  {
    icon: BarChart3,
    title: 'Budget & Financial Tools',
    desc: 'Know exactly where every dollar goes.',
  },
  {
    icon: FileText,
    title: 'Exportable Project Reports',
    desc: 'PDF and CSV snapshots of your entire project.',
  },
];

const TRUST_ITEMS = [
  { icon: Clock, text: '7-day free trial' },
  { icon: CreditCard, text: "7-day free trial, card required. Cancel anytime before day 7 and you won't be charged." },
  { icon: Shield, text: 'Cancel any time' },
];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const passwordStrength = (p) => {
    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    return score;
  };
  const strength = passwordStrength(form.password);
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][strength];
  const strengthColor = ['', 'bg-red-500', 'bg-yellow-500', 'bg-[#359b95]', 'bg-green-500'][strength];

  const handle = async (e) => {
    e.preventDefault();
    const emailErr = validateEmail(form.email);
    if (emailErr) { setError(emailErr); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      // Capture meta cookies BEFORE the API call so they're sent in the body
      const metaCookies = getMetaCookies();
      // Fire pixel events first to get the event_id for deduplication
      const regEventId = trackCompleteRegistration();
      trackOaiRegistration();
      // Pass meta tracking fields to the backend for CAPI deduplication
      await register(form.email, form.password, form.name, {
        meta_event_id: regEventId,
        meta_fbc: metaCookies.meta_fbc,
        meta_fbp: metaCookies.meta_fbp,
      });
      // Enable Advanced Matching for future pixel events
      setAdvancedMatching(form.email);
      navigate('/onboarding');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: '#f0fafa' }}>

      {/* ─── Top accent bar ─────────────────────────────────────────────── */}
      <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, #359b95, #1f425c)' }} />

      {/* ─── Back to home link ──────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#359b95] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>
      </div>

      {/* ─── Main layout: two-column on desktop, stacked on mobile ────── */}
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">

          {/* ═══ Left column — Value proposition ═══════════════════════════ */}
          <div className="w-full lg:w-5/12 lg:sticky lg:top-8">
            {/* Logo */}
            <img
              src="/stratalogy-logo-transparent.png"
              alt="Stratalogy"
              className="w-40 h-auto mb-6"
            />

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-3" style={{ color: '#1f425c' }}>
              Start Your Free Trial
            </h1>
            <p className="text-lg mb-8" style={{ color: '#243549' }}>
              From breaking ground to handing over the keys — the permanent record of your property.
            </p>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-3 mb-8">
              {TRUST_ITEMS.map(({ icon: Icon, text }) => (
                <div
                  key={text}
                  className="flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium"
                  style={{ background: '#e6f5f4', color: '#1f6b66', border: '1px solid #b2dedd' }}
                >
                  <Icon className="h-4 w-4 shrink-0" style={{ color: '#359b95' }} />
                  {text}
                </div>
              ))}
            </div>

            {/* What you get */}
            <div className="space-y-5 mb-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                What you get
              </p>
              {VALUE_PROPS.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex gap-4 items-start">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: 'linear-gradient(135deg, #359b95, #1f425c)' }}
                  >
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: '#1f425c' }}>{title}</p>
                    <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>


          </div>

          {/* ═══ Right column — Registration form ═════════════════════════ */}
          <div className="w-full lg:w-7/12 lg:max-w-lg">
            <Card className="bg-white border border-slate-200 shadow-xl rounded-2xl">
              <CardContent className="px-6 sm:px-8 py-8">
                {/* Form header */}
                <div className="mb-6">
                  <h2 className="text-xl font-bold mb-1" style={{ color: '#1f425c' }}>
                    Create your account
                  </h2>
                  <p className="text-slate-500 text-sm">
                    7-day free trial, card required. Cancel anytime before day 7 and you won't be charged.
                  </p>
                </div>

                <form onSubmit={handle} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                      <AlertCircle className="h-4 w-4 shrink-0" />{error}
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-slate-700 text-sm font-medium">Full Name</Label>
                    <Input required placeholder="John Smith" value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      className="border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-[#359b95] h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-700 text-sm font-medium">Email</Label>
                    <Input type="email" required placeholder="you@example.com" value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      className="border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-[#359b95] h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-700 text-sm font-medium">Password</Label>
                    <div className="relative">
                      <Input type={show ? 'text' : 'password'} required placeholder="Min. 8 characters" value={form.password}
                        onChange={e => setForm({ ...form, password: e.target.value })}
                        className="border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-[#359b95] pr-10 h-11" />
                      <button type="button" onClick={() => setShow(!show)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {form.password && (
                      <div className="space-y-1">
                        <div className="flex gap-1">
                          {[1,2,3,4].map(i => (
                            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= strength ? strengthColor : 'bg-slate-200'}`} />
                          ))}
                        </div>
                        <p className="text-xs text-slate-500">Strength: <span className="font-semibold text-slate-700">{strengthLabel}</span></p>
                      </div>
                    )}
                  </div>
                  <Button type="submit" disabled={loading}
                    className="w-full text-white font-semibold py-3 mt-3 text-base hover:opacity-90 rounded-xl h-12"
                    style={{ background: 'linear-gradient(135deg, #359b95, #1f425c)', boxShadow: '0 4px 20px rgba(53,155,149,0.35)' }}>
                    {loading ? 'Creating account...' : 'Start Your Free Trial'}
                  </Button>
                </form>

                {/* Reassurance line */}
                <p className="text-center text-xs text-slate-400 mt-4">
                  By creating an account you agree to our{' '}
                  <Link to="/terms" className="underline hover:text-slate-600">Terms</Link> and{' '}
                  <Link to="/privacy" className="underline hover:text-slate-600">Privacy Policy</Link>.
                </p>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-3 text-xs text-slate-400">or</span>
                  </div>
                </div>

                <p className="text-center text-slate-500 text-sm">
                  Already have an account?{' '}
                  <Link to="/login" className="font-semibold hover:underline" style={{ color: '#359b95' }}>Sign in</Link>
                </p>
              </CardContent>
            </Card>

            {/* Footer links */}
            <div className="flex justify-center gap-4 mt-6">
              <Link to="/privacy" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Terms of Service</Link>
              <Link to="/refund-policy" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Refund Policy</Link>
              <Link to="/help" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Help</Link>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
